import { useState, useEffect } from 'react';
import { getAnbefalinger, FALLBACK_GALLERIER } from '../lib/cms';
import { imageUrl } from '../lib/images';

export default function TestimonialsSection({
  category = null,
  title = 'Det siger kunderne om Lunde Media',
  subtitle = 'Autentiske oplevelser & ægte ord fra mennesker foran kameraet',
  className = '',
  innerClassName = '',
  theme = 'dark',
  showFilter = false,
  limit = null,
}) {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(category || 'alle');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  useEffect(() => {
    let mounted = true;
    async function loadData() {
      setLoading(true);
      const data = await getAnbefalinger();
      if (mounted) {
        setTestimonials(data || []);
        setLoading(false);
      }
    }
    loadData();
    return () => {
      mounted = false;
    };
  }, []);

  const filterTabs = [
    { key: 'alle', label: 'Alle anbefalinger' },
    { key: 'bryllup', label: 'Bryllup' },
    { key: 'familie', label: 'Familie & Konfirmation' },
    { key: 'events', label: 'Events & Cykelløb' },
    { key: 'erhverv', label: 'Erhverv & Branding' },
  ];

  const filteredItems = testimonials.filter((item) => {
    if (category) {
      if (category === 'events' && (item.kategori === 'events' || item.kategori === 'cykelloeb')) return true;
      if (category === 'cykelloeb' && (item.kategori === 'events' || item.kategori === 'cykelloeb')) return true;
      return item.kategori === category;
    }
    if (activeTab === 'alle') return true;
    if (activeTab === 'familie') return item.kategori === 'familie';
    if (activeTab === 'bryllup') return item.kategori === 'bryllup';
    if (activeTab === 'events') return item.kategori === 'events' || item.kategori === 'cykelloeb';
    if (activeTab === 'erhverv') return item.kategori === 'erhverv';
    return true;
  });

  const displayItems = limit ? filteredItems.slice(0, limit) : filteredItems;

  // Determine images to slide through based on category or active testimonial
  const effectiveCategory = category || (displayItems[currentIndex]?.kategori) || 'bryllup';

  const categoryImagesMap = {
    cykelloeb: [
      '/_assets/467843501-1084374129755746-8742697337945456892-n.jpg',
      '/_assets/115820782-172952827564552-2790911280586450986-n.jpg',
      '/_assets/115803766-172952787564556-2756493644119945137-n.jpg',
      '/_assets/110340462-172952877564547-9114177833398868150-n.jpg',
      '/_assets/115803630-172952807564554-3070980139574409914-n.jpg',
      '/_assets/112597004-172952647564570-1166596095639646479-n.jpg',
    ],
    bryllup: [
      '/_assets/472331762-1068515591958554-2413136772439242105-n.jpg',
      '/_assets/323a0121.jpg',
      '/_assets/323a0400.jpg',
      '/_assets/469269727-1092454198947739-5094198924173448864-n.jpg',
      '/_assets/323a6058.jpg',
      '/_assets/323a0442.jpg',
    ],
    familie: [
      '/_assets/img-7476.jpg',
      '/_assets/img-7455-2.jpg',
      '/_assets/323a6361.jpg',
      '/_assets/121001566-192835678909600-4366089546131733324-n.jpg',
      '/_assets/img-7435.jpg',
      '/_assets/122139316-196386581887843-7973893422891536185-n.jpg',
    ],
    events: [
      '/_assets/75513566-168614701331698-9032259809111210225-n.jpg',
      '/_assets/487316894-1134600408683405-7489097942827672171-n.jpg',
      '/_assets/142081323-247059026820598-2713212477238493269-n.jpg',
      '/_assets/166714167-283183176541516-3142839106080478528-n.jpg',
      '/_assets/84387927-168614671331701-3333494197708786303-n.jpg',
    ],
    erhverv: [
      '/_assets/142081323-247059026820598-2713212477238493269-n.jpg',
      '/_assets/142299669-247059033487264-5481047819933199986-n.jpg',
      '/_assets/487214857-1134600378683408-2220849442786892437-n.jpg',
      '/_assets/166783092-283183186541515-3612883362797497453-n.jpg',
    ],
  };

  const imagesToCycle = categoryImagesMap[effectiveCategory] || categoryImagesMap.bryllup;

  // Auto-advance the active image every 3.8 seconds for smooth ambient movement
  useEffect(() => {
    if (!imagesToCycle || imagesToCycle.length <= 1) return;
    const timer = setInterval(() => {
      setActiveImageIndex((prev) => (prev + 1) % imagesToCycle.length);
    }, 3800);
    return () => clearInterval(timer);
  }, [imagesToCycle]);

  // Reset image index when category changes
  useEffect(() => {
    setActiveImageIndex(0);
  }, [effectiveCategory]);

  const isDark = theme === 'dark';
  const isWhite = theme === 'white';

  return (
    <section
      id="anbefalinger"
      className={`transition-colors ${
        isWhite
          ? 'bg-white text-[#141416]'
          : isDark
          ? 'py-10 md:py-14 bg-[#1C1C1F] text-[#F3EFEA]'
          : 'py-10 md:py-14 bg-[#FAF8F5] text-[#141416] border-y border-stone-200'
      } ${className}`}
      aria-label="Kundeanbefalinger"
    >
      <div className={`max-w-7xl mx-auto px-6 md:px-8 ${innerClassName}`}>
        {/* Header - Kompakt & elegant */}
        <div className="flex flex-row items-end justify-between gap-4 mb-6 sm:mb-8">
          <div className="text-left max-w-2xl space-y-1.5">
            <h2
              className={`text-2xl sm:text-3xl font-serif font-medium tracking-tight ${
                isDark ? 'text-[#F4EFEA]' : 'text-stone-900'
              }`}
            >
              {title}
            </h2>

            {subtitle && (
              <p className={`text-xs sm:text-sm font-sans leading-relaxed ${isDark ? 'text-zinc-400' : 'text-stone-600'}`}>
                {subtitle}
              </p>
            )}
          </div>

          {/* Knapper over anmeldelserne til højre */}
          {displayItems.length > 1 && (
            <div className="flex items-center gap-1.5 shrink-0 self-end">
              <button
                type="button"
                onClick={() => setCurrentIndex((prev) => (prev > 0 ? prev - 1 : displayItems.length - 1))}
                className={`w-8 h-8 rounded-full flex items-center justify-center border text-xs transition-all duration-200 shadow-xs ${
                  isDark
                    ? 'border-white/20 bg-zinc-900/90 text-white hover:bg-white/10 hover:border-[#B89B72]'
                    : 'border-stone-300 bg-white text-stone-800 hover:bg-stone-100 hover:border-stone-400'
                }`}
                aria-label="Forrige anbefaling"
              >
                <i className="fa-solid fa-chevron-left" aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={() => setCurrentIndex((prev) => (prev < displayItems.length - 1 ? prev + 1 : 0))}
                className={`w-8 h-8 rounded-full flex items-center justify-center border text-xs transition-all duration-200 shadow-xs ${
                  isDark
                    ? 'border-white/20 bg-zinc-900/90 text-white hover:bg-white/10 hover:border-[#B89B72]'
                    : 'border-stone-300 bg-white text-stone-800 hover:bg-stone-100 hover:border-stone-400'
                }`}
                aria-label="Næste anbefaling"
              >
                <i className="fa-solid fa-chevron-right" aria-hidden="true" />
              </button>
            </div>
          )}
        </div>

        {/* Loading state */}
        {loading ? (
          <div className="py-8 text-center text-zinc-400 text-sm">
            <i className="fa-solid fa-circle-notch animate-spin text-[#B89B72] mr-2" aria-hidden="true" />
            <span>Henter anbefalinger...</span>
          </div>
        ) : displayItems.length === 0 ? (
          <div className="text-center py-6 text-zinc-500 text-sm">
            Ingen anbefalinger fundet i denne kategori.
          </div>
        ) : (
          /* Testimonials Container with Dynamic Moving Photo to the Left */
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 lg:gap-8 items-stretch w-full mx-auto">
            {/* Left: Dynamic slideshow / ambient animated image matching the theme */}
            <div className="md:col-span-4 hidden md:block">
              <div className="relative w-full h-full min-h-[300px] overflow-hidden border border-stone-200/90 shadow-sm bg-stone-100 group">
                {imagesToCycle.map((imgSrc, imgIdx) => (
                  <div
                    key={imgSrc + imgIdx}
                    className={`absolute inset-0 transition-all duration-1000 ease-in-out ${
                      imgIdx === activeImageIndex
                        ? 'opacity-100 scale-100'
                        : 'opacity-0 scale-105 pointer-events-none'
                    }`}
                  >
                    <img
                      src={imageUrl(imgSrc, 'width=600,quality=85,format=auto')}
                      alt="Stemning fra Lunde Media"
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                  </div>
                ))}

                {/* Subtelt tag i bunden af billedet */}
                <div className="absolute bottom-3 left-3 right-3 z-10 flex items-center justify-between text-[11px] text-white/90">
                  <span className="font-serif italic drop-shadow-sm">Lunde Media</span>
                  <div className="flex gap-1">
                    {imagesToCycle.map((_, dotI) => (
                      <span
                        key={dotI}
                        onClick={() => setActiveImageIndex(dotI)}
                        className={`h-1 cursor-pointer rounded-full transition-all duration-300 ${
                          dotI === activeImageIndex ? 'w-3 bg-white' : 'w-1 bg-white/40'
                        }`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Testimonials Carousel Card */}
            <div className="md:col-span-8 flex flex-col justify-between">
              {/* Carousel Slides Container */}
              <div className="overflow-hidden h-full relative">
                <div
                  className="flex transition-transform duration-500 ease-out h-full"
                  style={{ transform: `translateX(-${currentIndex * 100}%)` }}
                >
                  {displayItems.map((item, idx) => (
                    <div key={item.id || idx} className="w-full shrink-0 h-full flex flex-col">
                      <div
                        className={`flex flex-col justify-between p-6 sm:p-7 border transition-all duration-300 h-full ${
                          isDark
                            ? 'bg-zinc-950/80 border-white/10 shadow-lg'
                            : 'bg-white border-stone-200/90 shadow-sm'
                        }`}
                      >
                        <div>
                          {/* Rating Stars & Quote Icon */}
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-1 text-[#CBB28D] text-sm">
                              {[...Array(item.stjerner || 5)].map((_, i) => (
                                <i key={i} className="fa-solid fa-star" aria-hidden="true" />
                              ))}
                            </div>
                            <i
                              className={`fa-solid fa-quote-right text-xl sm:text-2xl ${
                                isDark ? 'text-[#B89B72]/20' : 'text-stone-300'
                              }`}
                              aria-hidden="true"
                            />
                          </div>

                          {/* Body Text */}
                          <div
                            className={`font-sans text-sm sm:text-base leading-relaxed space-y-2.5 font-normal italic ${
                              isDark ? 'text-zinc-200' : 'text-zinc-700'
                            }`}
                            dangerouslySetInnerHTML={{ __html: item.tekst }}
                          />
                        </div>

                        {/* Author Info */}
                        <div
                          className={`mt-5 pt-3.5 border-t flex items-center justify-between gap-4 ${
                            isDark ? 'border-white/10' : 'border-stone-200'
                          }`}
                        >
                          <div>
                            <h3
                              className={`font-serif text-base sm:text-lg font-medium tracking-tight ${
                                isDark ? 'text-white' : 'text-zinc-900'
                              }`}
                            >
                              {item.navn}
                            </h3>
                            {item.begivenhed && (
                              <p className="text-[11px] sm:text-xs uppercase tracking-wider text-[#B89B72] font-medium mt-0.5">
                                {item.begivenhed}
                              </p>
                            )}
                          </div>
                          <div
                            className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${
                              isDark ? 'bg-[#B89B72]/15 text-[#CBB28D] border border-[#B89B72]/30' : 'bg-stone-100 text-zinc-700'
                            }`}
                            title="Verificeret kunde"
                          >
                            <i className="fa-solid fa-check text-[11px]" aria-hidden="true" />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
